import { InstallationDetail } from "@tether-web-portal/types/InstallationDetail";
import { OrgSettings } from "@tether-web-portal/api/auth/OrgSettingService";
import { CircuitNode } from "@tether-web-portal/types/CircuitNode";
import dayjs from "@tether-web-portal/dayjs-setup";
import { SeriesType } from "./SeriesType";
import {
  CircuitReading,
  CombinedPropertyReadings,
  filterVoltDropTotals,
  getCircuitSeriesData,
  isStackedBarGraph,
  seriesIsPower,
} from "./GraphUtils";
import { SelectedGraphType } from "../../property/graph/PropertyReadingsGraph";

export type SeriesDataPoint = { timestamp: number; value: number };

export const toHighchartsData = (points: SeriesDataPoint[]): [number, number][] =>
  points.map((point) => [point.timestamp, point.value]);

export const mapUsageReadingsToPoints = <T extends { startTimestamp: string }>(
  readings: T[] | undefined,
  valueKey: keyof T,
): SeriesDataPoint[] => {
  if (!Array.isArray(readings)) return [];

  return readings
    .map((reading) => {
      const rawValue = reading[valueKey];
      if (rawValue === null || rawValue === undefined || typeof rawValue !== "number") return null;

      return {
        timestamp: dayjs(reading.startTimestamp).valueOf(),
        value: rawValue as number,
      };
    })
    .filter((point): point is SeriesDataPoint => Boolean(point));
};

export const mapTupleReadingsToPoints = (
  readings: Array<[number, number | null | undefined]> | undefined,
): SeriesDataPoint[] => {
  if (!Array.isArray(readings)) return [];

  return readings
    .map(([timestamp, value]) => {
      if (value === null || value === undefined || typeof value !== "number") return null;
      return { timestamp, value };
    })
    .filter((point): point is SeriesDataPoint => Boolean(point));
};

type CollectSeriesPointsArgs = {
  combinedReadings: CombinedPropertyReadings;
  seriesType: SeriesType;
  orgSettings: OrgSettings;
  propertyDetails: InstallationDetail<number | boolean | string>[];
  circuitNodeTree?: CircuitNode[];
  /**
   * Optional predicate to further filter circuits beyond volt-drop filtering.
   * Useful to align stats/highlights with whatever the graph actually renders.
   */
  circuitFilter?: (circuit: CircuitReading) => boolean;
  /**
   * Optional list of location ids (rooms/outsides/occupancy/circuits) to exclude.
   * Use this to keep stats/highlights in sync with hidden series.
   */
  excludedLocationIds?: string[];
};

/**
 * Collect timestamp/value pairs across all supported sources for a series type.
 * Power readings reuse getCircuitSeriesData so transforms stay aligned.
 */
export const collectSeriesPoints = ({
  combinedReadings,
  seriesType,
  orgSettings,
  propertyDetails,
  circuitNodeTree,
  circuitFilter,
  excludedLocationIds = [],
}: CollectSeriesPointsArgs): SeriesDataPoint[] => {
  const points: SeriesDataPoint[] = [];

  points.push(...collectTupleReadings(combinedReadings, seriesType, excludedLocationIds));

  if (seriesIsPower(seriesType)) {
    const filteredCircuits = (circuitNodeTree
      ? combinedReadings.circuits.filter((c) => filterVoltDropTotals(c, circuitNodeTree))
      : combinedReadings.circuits
    )
      .filter((c) => !excludedLocationIds.includes(c.id))
      .filter((c) => (circuitFilter ? circuitFilter(c) : true));

    filteredCircuits.forEach((circuit) => {
      const circuitValues = getCircuitSeriesData(circuit, seriesType, orgSettings, propertyDetails);
      points.push(...circuitValues);
    });
  }

  switch (seriesType) {
    case SeriesType.Water:
      combinedReadings.waterOutsides.forEach((water) => {
        if (excludedLocationIds.includes(water.id)) return;
        points.push(...mapUsageReadingsToPoints(water.waterUsage, "waterUsage"));
      });
      break;
    case SeriesType.Gas:
      combinedReadings.gasOutsides.forEach((gas) => {
        if (excludedLocationIds.includes(gas.id)) return;
        points.push(...mapUsageReadingsToPoints(gas.gasUsage, "gasUsage"));
      });
      break;
    case SeriesType.GasCost:
      combinedReadings.gasOutsides.forEach((gas) => {
        if (excludedLocationIds.includes(gas.id)) return;
        points.push(...mapUsageReadingsToPoints(gas.gasUsageCost, "gasCostUsage"));
      });
      break;
    case SeriesType.OccupancyEntryCount:
      combinedReadings.occupancyEntryLocations.forEach((rl) => {
        if (excludedLocationIds.includes(rl.id)) return;
        points.push(...mapTupleReadingsToPoints(rl.readings[SeriesType.OccupancyEntryCount]));
      });
      break;
    default:
      break;
  }

  return points;
};

const collectTupleReadings = (
  combinedReadings: CombinedPropertyReadings,
  seriesType: SeriesType,
  excludedLocationIds: string[],
): SeriesDataPoint[] => {
  const tuplePoints: SeriesDataPoint[] = [];

  combinedReadings.roomLocations.forEach((rl) => {
    if (excludedLocationIds.includes(rl.id)) return;
    tuplePoints.push(...mapTupleReadingsToPoints(rl.readings[seriesType]));
  });

  combinedReadings.outsides.forEach((outside) => {
    if (excludedLocationIds.includes(outside.id)) return;
    tuplePoints.push(...mapTupleReadingsToPoints(outside.readings[seriesType]));
  });

  return tuplePoints;
};

/**
 * Determines whether values should be aggregated for stacked bar charts.
 * Only aggregates when the series type is a stacked bar AND we're displaying as BAR (not LINE).
 */
export const shouldAggregateValues = (
  seriesType: SeriesType,
  graphTypeOverride?: SelectedGraphType,
): boolean => {
  return isStackedBarGraph(seriesType) && (!graphTypeOverride || graphTypeOverride === "BAR");
};

/**
 * For stacked charts we need timestamp-level totals to compute statistics/highlights.
 */
export const aggregateStackedPoints = (points: SeriesDataPoint[]): SeriesDataPoint[] => {
  const timestampMap = new Map<number, number>();

  points.forEach(({ timestamp, value }) => {
    const current = timestampMap.get(timestamp) ?? 0;
    timestampMap.set(timestamp, current + value);
  });

  return Array.from(timestampMap.entries()).map(([timestamp, value]) => ({ timestamp, value }));
};

export interface RawStatistics {
  max: { value: number; timestamp: number; count: number };
  min: { value: number; timestamp: number; count: number };
  average: { value: number };
  sum: number;
}

/**
 * Calculates min, max, average, and sum statistics from an array of timestamped values.
 * For min/max, tracks the most recent occurrence when there are ties.
 */
export const computeStatisticsFromPoints = (points: SeriesDataPoint[]): RawStatistics | null => {
  if (points.length === 0) return null;

  let { value: minValue, timestamp: minTimestamp } = points[0];
  let minCount = 1;

  let { value: maxValue, timestamp: maxTimestamp } = points[0];
  let maxCount = 1;

  let sum = points[0].value;

  for (let i = 1; i < points.length; i++) {
    const { value, timestamp } = points[i];

    sum += value;

    if (value < minValue) {
      minValue = value;
      minTimestamp = timestamp;
      minCount = 1;
    } else if (value === minValue) {
      minTimestamp = timestamp;
      minCount++;
    }

    if (value > maxValue) {
      maxValue = value;
      maxTimestamp = timestamp;
      maxCount = 1;
    } else if (value === maxValue) {
      maxTimestamp = timestamp;
      maxCount++;
    }
  }

  const average = sum / points.length;

  return {
    max: { value: maxValue, timestamp: maxTimestamp, count: maxCount },
    min: { value: minValue, timestamp: minTimestamp, count: minCount },
    average: { value: average },
    sum,
  };
};
